import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { ServiceAppointmentStatus } from './service-appointment-status.model';

/**
 * A drone service appointment request, built from the hub's service-appointment JSON
 * (`user-service-appointments-controller.ts`). Synced both ways with Zoho FSM.
 *
 * Fields the hub didn't send stay `undefined` (an explicit `null` stays `null`); display fallbacks belong
 * in templates.
 */
export class ServiceAppointment extends Base {
  // Set on create
  organizationId?: string | null;
  droneId?: string | null;
  /** YYYY-MM-DD */
  preferredDate?: string | null;
  /** morning, afternoon, evening or anytime */
  preferredTime?: string | null;
  issueDescription?: string | null;

  // Read-only fields
  /** Hub-controlled lifecycle; defaults to 'pending' only when the hub omits it. */
  status: ServiceAppointmentStatus;
  workOrderId?: string | null;
  /** Zoho Request ID (from the initial sync) */
  zohoFsmRequestId?: string | null;
  /** Zoho Service Appointment ID (from the webhook) */
  zohoFsmAppointmentId?: string | null;
  /** Zoho Work Order ID (from the webhook) */
  zohoFsmWorkOrderId?: string | null;
  /** Set when a technician creates the appointment */
  scheduledDate?: string | null;
  technicianName?: string | null;
  notes?: string | null;
  cancelledAt?: string | null;
  cancelledReason?: string | null;
  createdByProfileId?: string | null;

  // Display fields the hub joins in
  droneName?: string | null;
  droneSerialNumber?: string | null;
  createdByName?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.organizationId = nullableString(source['organizationId']);
    this.droneId = nullableString(source['droneId']);
    this.preferredDate = nullableString(source['preferredDate']);
    this.preferredTime = nullableString(source['preferredTime']);
    this.issueDescription = nullableString(source['issueDescription']);
    this.status = (nullableString(source['status']) ?? 'pending') as ServiceAppointmentStatus;
    this.workOrderId = nullableString(source['workOrderId']);
    this.zohoFsmRequestId = nullableString(source['zohoFsmRequestId']);
    this.zohoFsmAppointmentId = nullableString(source['zohoFsmAppointmentId']);
    this.zohoFsmWorkOrderId = nullableString(source['zohoFsmWorkOrderId']);
    this.scheduledDate = nullableString(source['scheduledDate']);
    this.technicianName = nullableString(source['technicianName']);
    this.notes = nullableString(source['notes']);
    this.cancelledAt = nullableString(source['cancelledAt']);
    this.cancelledReason = nullableString(source['cancelledReason']);
    this.createdByProfileId = nullableString(source['createdByProfileId']);
    this.droneName = nullableString(source['droneName']);
    this.droneSerialNumber = nullableString(source['droneSerialNumber']);
    this.createdByName = nullableString(source['createdByName']);
  }
}
